import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PolicyRule } from '@ward/core';
import { api, useAction, useApi } from '../../api';
import { Ago, DecisionTag, Empty, fmtNum, MutationError, Notice, Q, Section } from '../../ui/kit';

/**
 * Shared pieces of the Protect → Threat protection tabs: Web, Downloads, Privacy (P4–P6).
 * API: GET/PUT /api/v1/protection/settings (apps/server/src/routes/mgmt/protection.ts).
 */

export interface BundleStatus {
  version: number;
  compiledAt: string | null;
  dirty: boolean;
  lastError: string | null;
  stats: {
    webDomains?: number; webByCategory?: Record<string, number>; webTruncated?: boolean; allowDomains?: number; disableIds?: number; bytes?: number;
    reputableDomains?: number; trackerDomains?: number; trackerTruncated?: boolean;
  };
}

export interface ProtectionSettingsResponse {
  settings: Record<string, any>;
  defaults: Record<string, any>;
  categories: { security: string[]; privacy: string[] };
  bundle: BundleStatus | null;
  /** Server master switch (THREAT_PROTECTION). 'off': web / download settings are saved but not applied. */
  threatProtection?: 'on' | 'off';
}

export const threatOff = (d: ProtectionSettingsResponse) => d.threatProtection === 'off';

/** Shown on the Web and Downloads pages while the server's threat protection switch is off. */
export function ThreatOffBanner() {
  return (
    <Notice tone="warn" role="note" data-testid="threat-protection-off">
      <b>Threat protection is not enabled on this server (THREAT_PROTECTION=off) — settings are saved but not applied.</b>{' '}
      Browsers receive these protections switched off until the server operator turns threat protection on.
    </Notice>
  );
}

export const NOT_APPLIED = ' (not applied while threat protection is off on this server)';

export interface SaveResult { settings: Record<string, any>; changed: string[]; bundle: BundleStatus | null; publishError: string | null }

export const useProtectionSettings = () => useApi<ProtectionSettingsResponse>('/protection/settings');

export function useSaveProtection(onDone: (r: SaveResult) => void) {
  return useAction((body: Record<string, unknown>) => api<SaveResult>('/protection/settings', { method: 'PUT', body }), onDone);
}

export const CATEGORY_INFO: Record<string, [string, string]> = {
  malware: ['Malware distribution', 'Sites that host or spread malicious software.'],
  phishing: ['Phishing', 'Sites that imitate trusted brands to trick people into giving away information.'],
  credential_theft: ['Credential theft', 'Fake sign-in pages that harvest passwords and one-time codes.'],
  c2: ['Command and control', 'Servers that infected devices contact for instructions.'],
  cryptomining: ['Cryptomining', 'Pages that mine cryptocurrency in the browser using the device’s power.'],
  scam: ['Scams', 'Fraud such as fake shops and tech-support scams.'],
  malicious_redirect: ['Malicious redirects', 'Redirectors that lead to malicious pages.'],
  malicious_extension: ['Malicious extension infrastructure', 'Domains used by known malicious browser extensions.'],
  tracker: ['Trackers', 'Third-party analytics and advertising trackers that follow people across sites.'],
  fingerprinting: ['Fingerprinting', 'Scripts that identify browsers from their characteristics instead of cookies.'],
};
export const categoryLabel = (c: string) => CATEGORY_INFO[c]?.[0] ?? c;

/** Splits a textarea into distinct, trimmed, lower-cased entries. */
export const lines = (s: string) => [...new Set(s.split(/[\s,]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];

/**
 * What browsers currently receive (version, when, what): one status at the top of Threat protection,
 * covering the Web, Downloads and Privacy settings, which are published together as one signed bundle.
 */
export function BundleStatusPanel({ bundle, settings }: { bundle: BundleStatus | null; settings?: Record<string, any> }) {
  const s = bundle?.stats;
  return (
    <div className="mb-16" data-testid="bundle-status">
      <details className="notice">
        <summary>
          <b>Published to browsers:</b>{' '}
          {bundle ? <>protection bundle v{bundle.version} · <Ago at={bundle.compiledAt} />{bundle.dirty && <span className="sub"> · update pending</span>}</> : 'not published yet'}
        </summary>
        {!bundle || !s ? (
          <p className="sub">Nothing published yet. The first browser that syncs (or the next save) publishes the protection bundle.</p>
        ) : (
          <dl className="facts">
            {s.bytes !== undefined && <><dt>Size</dt><dd>{fmtNum(Math.round(s.bytes / 1024))} KB</dd></>}
            {s.webDomains !== undefined && <><dt>Blocked domains</dt><dd>{fmtNum(s.webDomains)}{s.webTruncated ? ' (capped)' : ''}{s.allowDomains !== undefined && <span className="sub"> · {fmtNum(s.allowDomains)} allowed</span>}</dd></>}
            {s.reputableDomains !== undefined && <><dt>Popular sites list</dt><dd>{s.reputableDomains ? `${fmtNum(s.reputableDomains)} domains` : 'not sent (warning off)'}</dd></>}
            {s.trackerDomains !== undefined && <><dt>Tracker domains</dt><dd>{settings?.privacy?.enabled || s.trackerDomains ? `${fmtNum(s.trackerDomains)}${s.trackerTruncated ? ' (capped)' : ''}` : 'not sent (privacy off)'}</dd></>}
          </dl>
        )}
        <p className="explain">Web, download and privacy settings reach browsers together as one signed protection bundle. Browsers running a current Ward extension check for a new version together with their policy (every few minutes) and verify its signature before using it.</p>
      </details>
      {bundle?.lastError && <Notice tone="block" role="alert">The last update could not be published: {bundle.lastError}. Browsers keep the previous version.</Notice>}
    </div>
  );
}

/** The bundle status for the Threat protection section (shares the settings query with the tabs). */
export function PublishedToBrowsers() {
  const q = useProtectionSettings();
  if (!q.data) return null;
  return <BundleStatusPanel bundle={q.data.bundle} settings={q.data.settings} />;
}

export function SaveBar({ writable, busy, error, saved, onSave, label = 'Save and publish' }: { writable: boolean; busy: boolean; error: unknown; saved: SaveResult | null; onSave: () => void; label?: string }) {
  return (
    <>
      <MutationError error={error} />
      {saved && (
        saved.publishError
          ? <Notice tone="block" role="alert" data-testid="protection-saved">Saved, but publishing failed: {saved.publishError}. Browsers keep the previous version.</Notice>
          : <Notice tone="allow" role="status" data-testid="protection-saved">Saved{saved.bundle ? ` — protection bundle v${saved.bundle.version} is available to browsers.` : '.'}</Notice>
      )}
      {writable && <button className="btn primary mt-16" disabled={busy} onClick={onSave} data-testid="protection-save">{busy ? 'Saving…' : label}</button>}
    </>
  );
}

interface LensPolicy { id: string; name: string; enabled: boolean; summary: string; action: string; hits_7d: number; conditions: PolicyRule['conditions'] }

/** Policy lens: the policies whose conditions `match` an area, what they do and how often they matched. */
export function PolicyLens({ title, aside, match, emptyTitle, children }: { title: string; aside?: ReactNode; match: (c: PolicyRule['conditions']) => boolean; emptyTitle: string; children?: ReactNode }) {
  const q = useApi<{ items: LensPolicy[] }>('/policies');
  return (
    <Section title={title} aside={aside}>
      <Q q={q}>
        {(d) => {
          const rows = d.items.filter((p) => match(p.conditions));
          return rows.length === 0 ? <Empty title={emptyTitle} /> : (
            <table className="t"><tbody>{rows.map((p) => (
              <tr key={p.id} className={p.enabled ? undefined : "dim"}>
                <td><Link to={`/protect/policies/${p.id}`}>{p.name}</Link>{!p.enabled && <span className="sub"> (disabled)</span>}<div className="explain">{p.summary}</div></td>
                <td><DecisionTag d={p.action} /></td><td className="num sub">{p.hits_7d} matches (7d)</td>
              </tr>
            ))}</tbody></table>
          );
        }}
      </Q>
      {children}
    </Section>
  );
}
